import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableSortLabel,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  ComposedChart,
  Legend,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import * as XLSX from "xlsx";


import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";

import { db } from "../Config/firebase";
import {
  DIMENSOES_PSICOSSOCIAIS,
  classificarRiscoMatriz,
  normalizarRespostasPsicossociais,
  getCodigoPergunta,
} from "./psicossocialUtils";

const TEXTO_NAO_INFORMADO = "N\u00e3o informado";
const CORES_DEMOGRAFICAS = [
  "#3367d6",
  "#dc3912",
  "#ff9900",
  "#109618",
  "#990099",
  "#607d8b",
];

function media(lista) {
  if (!lista.length) return 0;
  return lista.reduce((soma, valor) => soma + valor, 0) / lista.length;
}

function agruparPor(lista, campo, fallback = TEXTO_NAO_INFORMADO) {
  return lista.reduce((map, item) => {
    const chave = String(item[campo] || "").trim() || fallback;

    if (!map.has(chave)) map.set(chave, []);

    map.get(chave).push(item);
    return map;
  }, new Map());
}

function obterCorRisco(valor) {
  const risco = classificarRiscoMatriz(valor);
  return risco.cor;
}

function obterCorTextoClassificacao(classificacao) {
  return ["baixo", "medio", "alto"].includes(classificacao?.nivel)
    ? "#111"
    : "#fff";
}

function TooltipDistribuicaoDimensoes({ active, payload }) {
  if (!active || !payload?.length) return null;

  const dados = payload[0]?.payload;

  return (
    <Paper sx={{ p: 1.5, maxWidth: 320 }}>
      <Typography fontWeight={800}>{dados.risco}</Typography>
      <Typography variant="body2" mb={1}>
        {dados.quantidade}{" "}
        {dados.quantidade === 1 ? "dimensão" : "dimensões"}
      </Typography>

      {dados.dimensoes.map((dimensao) => (
        <Typography key={dimensao.nome} variant="body2">
          {dimensao.nome}: <strong>{dimensao.media}</strong>
        </Typography>
      ))}
    </Paper>
  );
}

function ImagemGraficoImpressao({ src, alt }) {
  if (!src) return null;

  return (
    <Box
      component="img"
      className="grafico-imagem-impressao"
      src={src}
      alt={alt}
      sx={{ display: "none", width: "100%", height: "100%", objectFit: "contain" }}
    />
  );
}

function percentualPizza({ percent }) {
  if (!percent) return "";
  return `${Math.round(percent * 100)}%`;
}

const ORDEM_RISCO_MATRIZ = {
  muito_baixo: 1,
  baixo: 2,
  medio: 3,
  alto: 4,
  critico: 5,
};

function classificarCelulaMatriz(probabilidade, severidade) {
  return classificarRiscoMatriz(
    Number(probabilidade || 0) * Number(severidade || 0)
  );
}

function calcularDimensoesPorMatriz(respostas = {}) {
  return DIMENSOES_PSICOSSOCIAIS.map((dimensao) => {
    const valores = dimensao.perguntas
      .map((codigo) => {
        const item = respostas[codigo];

        if (!item || typeof item !== "object") return null;

        const risco = Number(item.risco || 0);

        return Number.isFinite(risco) && risco > 0 ? risco : null;
      })
      .filter((valor) => valor != null);

    const mediaRisco = valores.length
      ? Number(media(valores).toFixed(1))
      : 0;

    return {
      ...dimensao,
      total: mediaRisco,
      percentual: mediaRisco,
    };
  });
}

function montarRespostasMatriz(respostas = {}) {
  return Object.entries(respostas)
    .map(([codigo, dados]) => {
      if (!dados || typeof dados !== "object") return null;

      const probabilidade = Number(dados.probabilidade || 0);
      const severidade = Number(dados.severidade || 0);
      const risco = Number(dados.risco || probabilidade * severidade);

      if (!probabilidade || !severidade) return null;

      const classificacao = classificarCelulaMatriz(probabilidade, severidade);

      return {
        codigo,
        probabilidade,
        severidade,
        risco,
        classificacao,
      };
    })
    .filter(Boolean);
}

function MatrizRiscoVisual({ item }) {
  const severidades = [1, 2, 3, 4, 5];
  const probabilidades = [5, 4, 3, 2, 1];
  const severidadeLabels = {
    1: ["1", "(Insignificante=", "Risco muito baixo)"],
    2: ["2 (Menor=", "Risco baixo)"],
    3: ["3", "(Moderada=", "Risco Medio)"],
    4: ["4 (Maior =", "Risco Alto)"],
    5: ["5 (Catastrófica", "= Risco muito", "alto)"],
  };
  const probabilidadeLabels = {
    5: "5 (Quase Certa = Sempre)",
    4: "4 (Provavel = Frequentemente)",
    3: "3 (Possivel = As vezes)",
    2: "2 (Improvavel = Raramente)",
    1: "1 (Rara = Nunca)",
  };

  return (
    <Box sx={{ overflowX: "auto" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          textAlign: "center",
          fontSize: 13,
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                border: "2px solid #000",
                padding: 6,
                width: 32,
              }}
            />
            <th
              colSpan={2}
              style={{
                border: "2px solid #000",
                padding: 6,
                background: "#f2f2f2",
              }}
            />
            <th
              colSpan={5}
              style={{
                border: "2px solid #000",
                padding: 6,
                background: "#f2f2f2",
                fontWeight: 900,
              }}
            >
              SEVERIDADE
            </th>
          </tr>
          <tr>
            <th
              style={{
                border: "2px solid #000",
                padding: 6,
                width: 32,
              }}
            />
            <th
              colSpan={2}
              style={{
                border: "2px solid #000",
                padding: 6,
              }}
            >
              Escalas
            </th>

            {severidades.map((s) => (
              <th
                key={s}
                style={{
                  border: "2px solid #000",
                  padding: 6,
                  background:
                    s === 1
                      ? classificarRiscoMatriz(1).cor
                      : s === 2
                      ? classificarRiscoMatriz(5).cor
                      : s === 3
                      ? classificarRiscoMatriz(10).cor
                      : s === 4
                      ? classificarRiscoMatriz(15).cor
                      : classificarRiscoMatriz(20).cor,
                }}
              >
                {severidadeLabels[s].map((linha) => (
                  <div key={linha}>{linha}</div>
                ))}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {probabilidades.map((p, index) => (
            <tr key={p}>
              {index === 0 && (
                <td
                  rowSpan={probabilidades.length}
                  style={{
                    border: "2px solid #000",
                    padding: 6,
                    writingMode: "vertical-rl",
                    transform: "rotate(180deg)",
                    fontWeight: 900,
                    letterSpacing: 1,
                  }}
                >
                  PROBABILIDADE
                </td>
              )}
              <td
                style={{
                  border: "2px solid #000",
                  padding: 6,
                  fontWeight: 700,
              }}
            >
                {probabilidadeLabels[p]}
              </td>

              <td
                style={{
                  border: "2px solid #000",
                  padding: 6,
                }}
              >
                {p === 5
                  ? "Quase certa"
                  : p === 4
                  ? "Provável"
                  : p === 3
                  ? "Possível"
                  : p === 2
                  ? "Improvável"
                  : "Rara"}
              </td>

              {severidades.map((s) => {
                const valor = p * s;
                const marcado =
                  item?.probabilidade === p && item?.severidade === s;
                const classificacao = classificarCelulaMatriz(p, s);
                const cor = classificacao.cor;

                return (
                  <td
                    key={`${p}-${s}`}
                    style={{
                      border: "2px solid #000",
                      padding: 8,
                      background: marcado ? "#fff8d6" : "#fff",
                      boxShadow: marcado ? `inset 0 0 0 3px ${cor}` : "none",
                      fontWeight: marcado ? 800 : 400,
                      minWidth: 92,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        minWidth: 42,
                      }}
                    >
                      <span
                        style={{
                          display: "inline-block",
                          flex: "0 0 auto",
                          boxSizing: "border-box",
                          width: 14,
                          height: 14,
                          background: cor,
                          border: marcado ? "2px solid #111" : "1px solid #555",
                          marginRight: 6,
                        }}
                      />
                      <span
                        style={{
                          display: "inline-block",
                          minWidth: 18,
                          textAlign: "left",
                        }}
                      >
                        {valor}
                      </span>
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
}

export default function ResultadosPsicossocial() {
  const { token } = useParams();

  const [colaborador, setColaborador] = useState(null);
  const [respostas, setRespostas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");
 const [matrizSelecionada, setMatrizSelecionada] = useState(null);
  const [perguntasPorCodigo, setPerguntasPorCodigo] = useState({});
  const [ordenacaoTabela, setOrdenacaoTabela] = useState({
    campo: "pergunta",
    direcao: "asc",
  });
  const [filtroFuncao, setFiltroFuncao] = useState("");
  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [graficosImpressao, setGraficosImpressao] = useState({});
  const [dimensaoSelecionada, setDimensaoSelecionada] = useState(
    DIMENSOES_PSICOSSOCIAIS[0].id
  );

  useEffect(() => {
    carregar();
  }, [token]);

  async function carregar() {
    try {
      setLoading(true);
      setErro("");

      const colaboradorSnap = await getDoc(
        doc(db, "psicossocial_colaboradores", token)
      );

      if (!colaboradorSnap.exists()) {
        setErro("Link n\u00e3o encontrado.");
        return;
      }

      const colaboradorData = {
        id: colaboradorSnap.id,
        ...colaboradorSnap.data(),
      };

      const consultaRespostas = query(
          collection(db, "kpi_respostas"),
          where("tipoFormulario", "==", "PSICOSSOCIAL"),
          where("visivelResultadoPublico", "==", true),
          where("kpiId", "==", colaboradorData.kpiId),
          where("clienteId", "==", colaboradorData.clienteId)
      );
      const consultaPerguntas = query(
          collection(db, "kpi_perguntas"),
          where("kpiId", "==", colaboradorData.kpiId)
      );
      const [respostasSnap, perguntasSnap] = await Promise.all([
        getDocs(consultaRespostas),
        getDocs(consultaPerguntas),
      ]);
      const mapaPerguntas = {};

      perguntasSnap.docs.forEach((item) => {
        const pergunta = { id: item.id, ...item.data() };
        const codigo = getCodigoPergunta(pergunta) || pergunta.id;

        mapaPerguntas[codigo] = pergunta.texto || "";
      });

      setColaborador(colaboradorData);
      setPerguntasPorCodigo(mapaPerguntas);
      setRespostas(
        respostasSnap.docs.map((item) => {
          const data = item.data();

          return {
            id: item.id,
            ...data,
            respostas: normalizarRespostasPsicossociais(
              data.respostas || {},
              data.versaoPontuacaoPsicossocial
            ),
          };
        })
      );
  } catch (error) {
  console.error("ERRO RESULTADOS", error);

  setErro(
    `${error.code || ""} - ${error.message || "Erro ao carregar resultados"}`
  );
} finally {
      setLoading(false);
    }
  }


  const funcoes = useMemo(() => {
  return Array.from(
    new Set(
      respostas.map(
        (item) => item.funcao || TEXTO_NAO_INFORMADO
      )
    )
  ).sort();
}, [respostas]);

  const respostasFiltradas = useMemo(() => {
    return respostas.filter((item) => {
      const setor = item.setor || TEXTO_NAO_INFORMADO;
      const funcao = item.funcao || TEXTO_NAO_INFORMADO;

    
      if (filtroFuncao && funcao !== filtroFuncao) return false;

      return true;
    });
  }, [filtroFuncao, respostas]);

  function alterarOrdenacaoTabela(campo) {
    setOrdenacaoTabela((atual) => ({
      campo,
      direcao:
        atual.campo === campo && atual.direcao === "asc" ? "desc" : "asc",
    }));
  }

  function ordenarItensMatriz(itens) {
    const fator = ordenacaoTabela.direcao === "asc" ? 1 : -1;

    return [...itens].sort((a, b) => {
      let resultado = 0;

      if (ordenacaoTabela.campo === "resultado") {
        resultado = Number(a.risco || 0) - Number(b.risco || 0);
      } else if (ordenacaoTabela.campo === "risco") {
        resultado =
          (ORDEM_RISCO_MATRIZ[a.classificacao?.nivel] || 0) -
          (ORDEM_RISCO_MATRIZ[b.classificacao?.nivel] || 0);
      } else {
        resultado = ordenarCodigoPergunta(a.codigo, b.codigo);
      }

      return resultado ? resultado * fator : ordenarCodigoPergunta(a.codigo, b.codigo);
    });
  }

  const respostasComDimensoes = useMemo(() => {
  return respostasFiltradas.map((resposta) => ({
    ...resposta,
    dimensoes: calcularDimensoesPorMatriz(resposta.respostas || {}),
  }));
}, [respostasFiltradas]);

  const respostasIndividuais = useMemo(() => {
  return respostasFiltradas.map((resposta, index) => ({
    ...resposta,
    respondenteLabel: `Respondente ${index + 1}`,
    matriz: ordenarItensMatriz(
      montarRespostasMatriz(resposta.respostas || {}).map((item) => ({
        ...item,
        perguntaTexto: perguntasPorCodigo[item.codigo] || "",
      }))
    ),
  }));
}, [ordenacaoTabela, perguntasPorCodigo, respostasFiltradas]);

  const resumoDimensoes = useMemo(() => {
    return DIMENSOES_PSICOSSOCIAIS.map((dimensao) => {
      const valores = respostasComDimensoes
        .map(
          (resposta) =>
            resposta.dimensoes.find((item) => item.id === dimensao.id)
              ?.percentual
        )
        .filter((valor) => valor != null);

      return {
        nome: dimensao.nome,
        media: Number(media(valores).toFixed(1)),
        totalRespostas: valores.length,
      };
    });
  }, [respostasComDimensoes]);

  const graficoSetores = useMemo(() => {
    const grupos = agruparPor(respostasComDimensoes, "setor");

    return Array.from(grupos.entries())
      .map(([setor, lista]) => {
        const valores = lista
          .map(
            (resposta) =>
              resposta.dimensoes.find(
                (item) => item.id === dimensaoSelecionada
              )?.percentual
          )
          .filter((valor) => valor != null);

        return {
          setor,
          media: Number(media(valores).toFixed(1)),
          total: lista.length,
        };
      })
      .sort((a, b) => b.media - a.media);
  }, [dimensaoSelecionada, respostasComDimensoes]);

  const mediaGeral = useMemo(() => {
  return Math.round(
    media(resumoDimensoes.map((item) => item.media))
  );
}, [resumoDimensoes]);

const dimensaoMaisCritica = useMemo(() => {
  if (!resumoDimensoes.length) return "-";

  return [...resumoDimensoes]
    .sort((a, b) => b.media - a.media)[0];
}, [resumoDimensoes]);

const setorMaisCritico = useMemo(() => {
  if (!graficoSetores.length) return "-";

  return [...graficoSetores]
    .sort((a, b) => b.media - a.media)[0];
}, [graficoSetores]);

const funcaoMaisCritica = useMemo(() => {
  const grupos = agruparPor(
    respostasComDimensoes,
    "funcao"
  );

  const ranking = Array.from(grupos.entries()).map(
    ([funcao, lista]) => {
      const medias = lista
        .map(
          (resposta) =>
            resposta.dimensoes.find(
              (item) => item.id === dimensaoSelecionada
            )?.percentual
        )
        .filter((valor) => valor != null);

      return {
        funcao,
        media: Number(media(medias).toFixed(1)),
      };
    }
  );

  if (!ranking.length) return "-";

  return ranking.sort(
    (a, b) => b.media - a.media
  )[0];
}, [dimensaoSelecionada, respostasComDimensoes]);

const graficoFuncoes = useMemo(() => {
  const grupos = agruparPor(respostasComDimensoes, "funcao");

  return Array.from(grupos.entries())
    .map(([funcao, lista]) => {
      const medias = lista
        .map(
          (resposta) =>
            resposta.dimensoes.find(
              (item) => item.id === dimensaoSelecionada
            )?.percentual
        )
        .filter((valor) => valor != null);

      return {
        funcao,
        media: Number(media(medias).toFixed(1)),
        total: lista.length,
      };
    })
    .sort((a, b) => b.media - a.media);
}, [dimensaoSelecionada, respostasComDimensoes]);

const nomeDimensaoSelecionada =
  DIMENSOES_PSICOSSOCIAIS.find(
    (dimensao) => dimensao.id === dimensaoSelecionada
  )?.nome || "";

const distribuicaoRisco = useMemo(() => {
  const mapa = new Map();

  resumoDimensoes
    .filter((dimensao) => dimensao.totalRespostas > 0)
    .forEach((dimensao) => {
      const classificacao = classificarRiscoMatriz(dimensao.media);
      const label = classificacao.label;

      if (!mapa.has(label)) {
        mapa.set(label, {
          risco: label,
          quantidade: 0,
          cor: classificacao.cor,
          nivel: classificacao.nivel,
          dimensoes: [],
        });
      }

      mapa.get(label).quantidade += 1;
      mapa.get(label).dimensoes.push({
        nome: dimensao.nome,
        media: dimensao.media,
      });
    });

  return Array.from(mapa.values()).sort(
    (a, b) =>
      (ORDEM_RISCO_MATRIZ[a.nivel] || 0) -
      (ORDEM_RISCO_MATRIZ[b.nivel] || 0)
  );
}, [resumoDimensoes]);

const distribuicaoIdade = useMemo(() => {
  const ordem = [
    "Até 24 anos",
    "25 a 34 anos",
    "35 a 44 anos",
    "45 a 54 anos",
    "55 anos ou mais",
    TEXTO_NAO_INFORMADO,
  ];
  const totais = new Map(ordem.map((faixa) => [faixa, 0]));

  respostasFiltradas.forEach((resposta) => {
    const faixa = resposta.faixaEtaria || TEXTO_NAO_INFORMADO;
    totais.set(faixa, (totais.get(faixa) || 0) + 1);
  });

  return Array.from(totais.entries())
    .filter(([, quantidade]) => quantidade > 0)
    .map(([nome, quantidade], index) => ({
      nome,
      quantidade,
      cor: CORES_DEMOGRAFICAS[index % CORES_DEMOGRAFICAS.length],
    }));
}, [respostasFiltradas]);

const distribuicaoSexo = useMemo(() => {
  const ordem = ["Masculino", "Feminino", TEXTO_NAO_INFORMADO];
  const totais = new Map(ordem.map((sexo) => [sexo, 0]));

  respostasFiltradas.forEach((resposta) => {
    const sexo = resposta.sexo || TEXTO_NAO_INFORMADO;
    totais.set(sexo, (totais.get(sexo) || 0) + 1);
  });

  return Array.from(totais.entries())
    .filter(([, quantidade]) => quantidade > 0)
    .map(([nome, quantidade], index) => ({
      nome,
      quantidade,
      cor: CORES_DEMOGRAFICAS[index % CORES_DEMOGRAFICAS.length],
    }));
}, [respostasFiltradas]);

const dadosRadial = [
  {
    name: classificarRiscoMatriz(mediaGeral).label,
    value: mediaGeral,
    fill: obterCorRisco(mediaGeral),
  },
];

const dataRelatorio = new Intl.DateTimeFormat("pt-BR", {
  dateStyle: "long",
  timeStyle: "short",
}).format(new Date());

function ajustarLarguraColunas(worksheet, linhas) {
  if (!linhas.length) return;

  const colunas = Object.keys(linhas[0]);
  worksheet["!cols"] = colunas.map((coluna) => ({
    wch: Math.min(
      42,
      Math.max(
        String(coluna).length,
        ...linhas.map((linha) => String(linha[coluna] ?? "").length)
      ) + 2
    ),
  }));
}

function criarAba(workbook, nome, linhas) {
  const dados = linhas.length ? linhas : [{ Informacao: "Sem dados" }];
  const worksheet = XLSX.utils.json_to_sheet(dados);
  ajustarLarguraColunas(worksheet, dados);
  XLSX.utils.book_append_sheet(workbook, worksheet, nome);
}

function ordenarCodigoPergunta(a, b) {
  const codigoA = String(a || "");
  const codigoB = String(b || "");
  const numeroA = Number(codigoA.match(/\d+/)?.[0] || 0);
  const numeroB = Number(codigoB.match(/\d+/)?.[0] || 0);

  if (numeroA !== numeroB) return numeroA - numeroB;

  return codigoA.localeCompare(codigoB, "pt-BR", {
    numeric: true,
    sensitivity: "base",
  });
}

function baixarResultadosExcel() {
  const workbook = XLSX.utils.book_new();
  const empresa = colaborador?.clienteNome || "Empresa nao informada";

  const respostasPlanilha = respostas.map((resposta, index) => ({
    ...resposta,
    respondenteLabel: `Respondente ${index + 1}`,
    matriz: montarRespostasMatriz(resposta.respostas || {}),
  }));

  const linhas = [];

  respostasPlanilha.forEach((resposta) => {
    linhas.push([resposta.respondenteLabel]);
    linhas.push(["Pergunta", "Probabilidade", "Severidade", "Resultado", "Risco"]);

    [...resposta.matriz].sort((a, b) => ordenarCodigoPergunta(a.codigo, b.codigo))
      .forEach((item) => {
        const textoPergunta = perguntasPorCodigo[item.codigo]
          ? ` - ${perguntasPorCodigo[item.codigo]}`
          : "";

        linhas.push([
          `${item.codigo}${textoPergunta}`,
          item.probabilidade,
          item.severidade,
          item.risco,
          item.classificacao.label,
        ]);
      });

    linhas.push([]);
  });

  const worksheet = XLSX.utils.aoa_to_sheet(
    linhas.length ? linhas : [["Sem dados"]]
  );

  worksheet["!cols"] = [
    { wch: 62 },
    { wch: 14 },
    { wch: 12 },
    { wch: 10 },
    { wch: 16 },
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, "Respostas");

  const agora = new Date();
  const dataHora = [
    agora.getFullYear(),
    String(agora.getMonth() + 1).padStart(2, "0"),
    String(agora.getDate()).padStart(2, "0"),
    String(agora.getHours()).padStart(2, "0"),
    String(agora.getMinutes()).padStart(2, "0"),
  ].join("-");

  const nomeArquivoSimples = `resultados-psicossocial-${String(empresa)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "empresa"}-${dataHora}.xlsx`;

  XLSX.writeFile(workbook, nomeArquivoSimples);
}

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  function capturarGraficosParaImpressao() {
    const imagens = {};

    document.querySelectorAll("[data-grafico-impressao]").forEach((container) => {
      const chave = container.getAttribute("data-grafico-impressao");
      const svg =
        container.querySelector(".recharts-wrapper > svg.recharts-surface") ||
        container.querySelector("svg.recharts-surface");

      if (!chave || !svg) return;

      const copia = svg.cloneNode(true);
      copia.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      copia.setAttribute("width", svg.getAttribute("width") || svg.clientWidth);
      copia.setAttribute("height", svg.getAttribute("height") || svg.clientHeight);

      imagens[chave] = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
        copia.outerHTML
      )}`;
    });

    return imagens;
  }

  function imprimirRelatorio() {
    setGraficosImpressao(capturarGraficosParaImpressao());

    window.setTimeout(() => {
      window.print();
    }, 150);
  }

  return (
    <Box
      className={modoVisualizacao ? "visualizacao-relatorio" : ""}
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        p: { xs: 2, md: 4 },
        ...(modoVisualizacao && {
          backgroundColor: "#9e9e9e",
          "& .relatorio-conteudo": {
            maxWidth: "none",
            padding: 0,
            backgroundColor: "transparent",
            borderRadius: 0,
            boxShadow: "none",
          },
          "& .pagina-dashboard, & .pagina-dados": {
            width: "198mm",
            boxSizing: "border-box",
            mx: "auto",
            backgroundColor: "#fff",
          },
          "& .pagina-dashboard": {
            height: "285mm",
            minHeight: "285mm",
            overflow: "hidden",
            mb: "10mm",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          },
          "& .pagina-dashboard-conteudo": {
            width: "253.85mm",
            height: "365.38mm",
            boxSizing: "border-box",
            p: "7mm",
            transform: "scale(0.78)",
            transformOrigin: "top left",
          },
          "& .pagina-dados": {
            minHeight: "285mm",
            p: "7mm",
            backgroundImage:
              "repeating-linear-gradient(to bottom, #fff 0, #fff calc(285mm - 2px), #bdbdbd calc(285mm - 2px), #bdbdbd 285mm)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
          },
          "& .relatorio-conteudo .nao-imprimir": {
            display: "none",
          },
          "& .graficos-dashboard": {
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 2,
          },
          "& .graficos-dashboard > .dashboard-grid": {
            display: "contents",
          },
          "& .graficos-dashboard .MuiCard-root": {
            minWidth: 0,
            overflow: "hidden",
          },
          "& .card-radar, & .card-contexto": {
            gridColumn: "1 / -1",
          },
          "& .card-radar": { order: 1 },
          "& .card-contexto": { order: 2 },
          "& .card-indicador": { order: 3 },
          "& .card-faixas": { order: 4 },
          "& .card-setor": { order: 5 },
          "& .card-funcao": { order: 6 },
          "& .card-idade": { order: 7 },
          "& .card-sexo": { order: 8 },
          "& .resumo-cards": {
            display: "grid",
            gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
            gap: 1,
            mb: 2,
          },
          "& .resumo-cards .MuiCard-root": {
            minWidth: 0,
          },
          "& .grafico-radar": {
            height: "175px",
          },
          "& .grafico-indicador": {
            height: "115px",
          },
          "& .grafico-contexto": {
            height: "155px",
          },
          "& .grafico-faixas, & .grafico-grupo": {
            height: "125px",
          },
          "& .grafico-demografico": {
            height: "145px",
          },
          "& .pagina-dashboard .MuiCardContent-root": {
            p: 0.75,
          },
          "& .pagina-dashboard .MuiTypography-h5": {
            fontSize: "18px",
          },
          "& .pagina-dashboard .MuiTypography-h6": {
            fontSize: "11px",
            mb: 0.25,
          },
          "& .pagina-dados .MuiTableCell-root": {
            fontSize: "0.72rem",
            lineHeight: 1.25,
            py: 0.55,
            px: 0.8,
          },
          "& .pagina-dados .MuiCardContent-root": {
            p: 1.25,
          },
        }),
        "@media print": {
          p: 0,
          backgroundColor: "#fff",

          "& .nao-imprimir": {
            display: "none !important",
          },

          "& .relatorio-conteudo": {
            page: "relatorioPsicossocial",
            width: "100% !important",
            maxWidth: "none !important",
            margin: "0 !important",
            padding: "0 !important",
            boxSizing: "border-box !important",
            boxShadow: "none !important",
          },

          "& .pagina-dashboard, & .pagina-dados": {
            width: "100% !important",
            boxSizing: "border-box !important",
            background: "#fff !important",
          },

          "& .pagina-dashboard": {
            height: "285mm !important",
            minHeight: "285mm !important",
            padding: "0 !important",
          },

          "& .pagina-dashboard-conteudo": {
            width: "253.85mm !important",
            height: "365.38mm !important",
            boxSizing: "border-box !important",
            padding: "7mm !important",
            transform: "scale(0.78) !important",
            transformOrigin: "top left !important",
          },

          "& .pagina-dados": {
            padding: "4mm !important",
          },

          "& .graficos-dashboard": {
            display: "grid !important",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) !important",
            gap: "3mm !important",
          },

          "& .graficos-dashboard > .dashboard-grid": {
            display: "contents !important",
          },

          "& .graficos-dashboard .MuiCard-root": {
            minWidth: "0 !important",
            overflow: "hidden !important",
          },

          "& .card-radar, & .card-contexto": {
            gridColumn: "1 / -1 !important",
          },

          "& .card-radar": { order: "1 !important" },
          "& .card-contexto": { order: "2 !important" },
          "& .card-indicador": { order: "3 !important" },
          "& .card-faixas": { order: "4 !important" },
          "& .card-setor": { order: "5 !important" },
          "& .card-funcao": { order: "6 !important" },
          "& .card-idade": { order: "7 !important" },
          "& .card-sexo": { order: "8 !important" },

          "& .resumo-cards": {
            display: "grid !important",
            gridTemplateColumns: "repeat(5, minmax(0, 1fr)) !important",
            gap: "2mm !important",
            mb: "4mm !important",
          },

          "& .resumo-cards .MuiCard-root": {
            minWidth: "0 !important",
          },

          "& .resumo-cards .MuiCardContent-root": {
            padding: "4px !important",
          },

          "& .grafico-radar": {
            height: "175px !important",
          },

          "& .grafico-indicador": {
            height: "115px !important",
          },

          "& .grafico-contexto": {
            height: "155px !important",
          },

          "& .grafico-faixas, & .grafico-grupo": {
            height: "125px !important",
          },

          "& .grafico-demografico": {
            height: "145px !important",
          },

          "& [data-grafico-impressao] .recharts-responsive-container, & [data-grafico-impressao] .recharts-wrapper": {
            display: "none !important",
          },

          "& .grafico-imagem-impressao": {
            display: "block !important",
          },

          "& .recharts-cartesian-axis-tick-value, & .recharts-polar-angle-axis-tick-value": {
            fontSize: "9px !important",
          },

          "& .indicador-valor": {
            fontSize: "18px !important",
            mt: "-24px !important",
          },

          "& .MuiTypography-h6": {
            fontSize: "11px !important",
            marginBottom: "2px !important",
          },

          "& .MuiTypography-body1, & .MuiTypography-body2": {
            fontSize: "8px !important",
          },

          "& .MuiCardContent-root": {
            padding: "5px !important",
          },

          "& .pagina-dashboard > .MuiTypography-h5": {
            fontSize: "16px !important",
            lineHeight: "1.15 !important",
          },

          "& .pagina-dashboard > .MuiTypography-root": {
            marginBottom: "2px !important",
          },

          "& .pagina-dashboard .resumo-cards": {
            gap: "8px !important",
            marginBottom: "16px !important",
          },

          "& .pagina-dashboard-conteudo .graficos-dashboard": {
            gap: "16px !important",
          },

          "& .pagina-dashboard-conteudo .MuiCardContent-root": {
            padding: "6px !important",
          },

          "& .pagina-dashboard-conteudo .MuiTypography-h5": {
            fontSize: "18px !important",
          },

          "& .pagina-dashboard-conteudo .MuiTypography-h6": {
            fontSize: "11px !important",
            marginBottom: "2px !important",
          },

          "& .pagina-dashboard-conteudo .MuiTypography-subtitle1, & .pagina-dashboard-conteudo .MuiTypography-body1": {
            fontSize: "16px !important",
          },

          "& .pagina-dashboard-conteudo .MuiTypography-body2": {
            fontSize: "14px !important",
          },

          "& .pagina-dashboard": {
            overflow: "hidden !important",
            breakAfter: "page",
            pageBreakAfter: "always",
            breakInside: "avoid",
            pageBreakInside: "avoid",
          },

          "& .pagina-dados, & .pagina-dados .MuiCard-root": {
            overflow: "visible !important",
            breakInside: "auto !important",
            pageBreakInside: "auto !important",
          },

          "& .pagina-dados .MuiCard-root": {
            display: "block !important",
            marginBottom: "3mm !important",
          },

          "& .pagina-dados .MuiCardContent-root": {
            padding: "5px !important",
          },

          "& .pagina-dados .MuiTypography-h5": {
            fontSize: "16px !important",
            marginBottom: "3mm !important",
          },

          "& .pagina-dados .MuiTypography-h6": {
            fontSize: "10px !important",
          },

          "& .pagina-dados [style*='overflow-x']": {
            overflow: "visible !important",
          },

          "& .pagina-dados table": {
            width: "100% !important",
            tableLayout: "auto",
          },

          "& .pagina-dados thead": {
            display: "table-header-group",
          },

          "& .pagina-dados tr": {
            breakInside: "avoid !important",
            pageBreakInside: "avoid !important",
          },

          "& .pagina-dados .tabelas-grupos": {
            display: "block !important",
          },

          "& .pagina-dados .MuiTableCell-root": {
            fontSize: "8px !important",
            lineHeight: "1.15 !important",
            padding: "3px 5px !important",
          },
        },
      }}
    >
      <style>
        {`
          @page relatorioPsicossocial {
            size: A4 portrait;
            margin: 6mm;
          }

          @media print {
            html, body {
              width: auto;
              min-height: 297mm;
            }
          }
        `}
      </style>

      {modoVisualizacao && (
        <Stack
          className="nao-imprimir"
          direction="row"
          justifyContent="center"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Button
            variant="contained"
            color="inherit"
            onClick={() => setModoVisualizacao(false)}
          >
            Voltar
          </Button>
          <Button variant="contained" onClick={imprimirRelatorio}>
            Imprimir
          </Button>
        </Stack>
      )}

      <Paper
        key={modoVisualizacao ? "visualizacao" : "edicao"}
        className="relatorio-conteudo"
        sx={{ maxWidth: 1180, mx: "auto", p: { xs: 2, md: 4 } }}
      >
        {erro && <Alert severity="info">{erro}</Alert>}

        {!erro && (
          <>
            <Box className="pagina-dashboard">
            <Box className="pagina-dashboard-conteudo">
            <Typography variant="h5" fontWeight={700}>
              Relatório de Resultados da Pesquisa Psicossocial
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ color: "#666", fontWeight: 600, mb: 0.5 }}
            >
              Empresa: {colaborador?.clienteNome || "-"}
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={1}>
              Emitido em: {dataRelatorio}
            </Typography>

            <Typography color="text.secondary" mb={3}>
              {"Os dados abaixo s\u00e3o anonimizados. As respostas podem ser vistas por respondente, sem exibir o nome do colaborador."}
            </Typography>

            {!modoVisualizacao && (
              <Stack
                className="nao-imprimir"
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                mb={3}
              >
             

              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel>{"Fun\u00e7\u00e3o"}</InputLabel>
                <Select
                  value={filtroFuncao}
                  label={"Fun\u00e7\u00e3o"}
                  onChange={(event) => setFiltroFuncao(event.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {funcoes.map((funcao) => (
                    <MenuItem key={funcao} value={funcao}>
                      {funcao}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={baixarResultadosExcel}
                disabled={!respostas.length}
                sx={{ alignSelf: { xs: "stretch", md: "center" } }}
              >
                Baixar Excel
              </Button>

              <Button
                variant="outlined"
                onClick={() => setModoVisualizacao(true)}
                disabled={!respostas.length}
                sx={{ alignSelf: { xs: "stretch", md: "center" } }}
              >
                Visualizar relatório de impressão
              </Button>
              </Stack>
            )}

 <Box
  className="resumo-cards"
  sx={{
    display: "flex",
    flexWrap: "wrap",
    gap: 1.5,
    mb: 3,
  }}
>
  
  <Card
  variant="outlined"
  sx={{
    minWidth: 180,
    flex: "1 1 180px",
  }}
>
  <CardContent
    sx={{
      p: 1.5,
      "&:last-child": {
        pb: 1.5,
      },
    }}
  >
      <Typography color="text.secondary">
        Respostas
      </Typography>

      <Typography fontWeight={700}>
        {respostasFiltradas.length}
      </Typography>
    </CardContent>
  </Card>

 <Card
  variant="outlined"
  sx={{
    minWidth: 180,
    flex: "1 1 180px",
  }}
>
  <CardContent
    sx={{
      p: 1.5,
      "&:last-child": {
        pb: 1.5,
      },
    }}
  >
      <Typography color="text.secondary">
        Resultado Médio da Matriz
      </Typography>

      <Typography fontWeight={700}>
        {mediaGeral} - {classificarRiscoMatriz(mediaGeral).label}
      </Typography>
    </CardContent>
  </Card>

<Card
  variant="outlined"
  sx={{
    minWidth: 180,
    flex: "1 1 180px",
  }}
>
  <CardContent
    sx={{
      p: 1.5,
      "&:last-child": {
        pb: 1.5,
      },
    }}
  >
      <Typography color="text.secondary">
        Dimensão Crítica
      </Typography>

      <Typography fontWeight={700}>
        {dimensaoMaisCritica?.nome || "-"}
      </Typography>
    </CardContent>
  </Card>

  <Card
  variant="outlined"
  sx={{
    minWidth: 180,
    flex: "1 1 180px",
  }}
>
  <CardContent
    sx={{
      p: 1.5,
      "&:last-child": {
        pb: 1.5,
      },
    }}
  >
      <Typography color="text.secondary">
        Setor Crítico
      </Typography>

      <Typography fontWeight={700}>
        {setorMaisCritico?.setor || "-"}
      </Typography>
    </CardContent>
  </Card>

 <Card
  variant="outlined"
  sx={{
    minWidth: 180,
    flex: "1 1 180px",
  }}
>
  <CardContent
    sx={{
      p: 1.5,
      "&:last-child": {
        pb: 1.5,
      },
    }}
  >
      <Typography color="text.secondary">
        Função Crítica
      </Typography>

      <Typography fontWeight={700}>
        {funcaoMaisCritica?.funcao || "-"}
      </Typography>
    </CardContent>
  </Card>
</Box>            

<Box className="graficos-dashboard">
<Box
  className="dashboard-grid"
  sx={{
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      lg: "1.2fr 0.8fr",
    },
    gap: 3,
    mb: 3,
  }}
>
  <Card className="evitar-quebra card-radar" variant="outlined" sx={{ borderRadius: 3 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={800} mb={2}>
        Mapa Radar das Dimensões
      </Typography>

      <Box
        className="grafico-radar"
        data-grafico-impressao="radar"
        sx={{ width: "100%", height: 220 }}
      >
        <ResponsiveContainer>
          <RadarChart
            data={resumoDimensoes}
            outerRadius="68%"
            margin={{ top: 5, right: 45, bottom: 5, left: 45 }}
          >
            <PolarGrid />
            <PolarAngleAxis dataKey="nome" />
            <PolarRadiusAxis domain={[0, 25]} />
            <Radar
              dataKey="media"
              stroke="#1976d2"
              fill="#1976d2"
              fillOpacity={0.4}
              isAnimationActive={!modoVisualizacao}
            />
            <Tooltip />
          </RadarChart>
        </ResponsiveContainer>
        <ImagemGraficoImpressao
          src={graficosImpressao.radar}
          alt="Mapa Radar das Dimensões"
        />
      </Box>
    </CardContent>
  </Card>

  <Card className="evitar-quebra card-indicador" variant="outlined" sx={{ borderRadius: 3 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={800} mb={2}>
        Indicador Geral
      </Typography>

      <Box
        className="grafico-indicador"
        data-grafico-impressao="indicador"
        sx={{ width: "100%", height: 160 }}
      >
        <ResponsiveContainer>
          <RadialBarChart
            innerRadius="65%"
            outerRadius="88%"
            data={dadosRadial}
            startAngle={180}
            endAngle={0}
            margin={{ top: 5, right: 20, bottom: 0, left: 20 }}
          >
            <PolarAngleAxis type="number" domain={[0, 25]} tick={false} />
            <RadialBar
              background
              clockWise
              dataKey="value"
              isAnimationActive={!modoVisualizacao}
            />
            <Tooltip />
          </RadialBarChart>
        </ResponsiveContainer>
        <ImagemGraficoImpressao
          src={graficosImpressao.indicador}
          alt="Indicador Geral"
        />
      </Box>

      <Typography
        className="indicador-valor"
        align="center"
        sx={{
          fontSize: 42,
          fontWeight: 900,
          color: obterCorRisco(mediaGeral),
          mt: -6,
        }}
      >
        {mediaGeral}
      </Typography>
      <Typography align="center" fontWeight={800} mt={1}>
        {classificarRiscoMatriz(mediaGeral).label} -{" "}
        {classificarRiscoMatriz(mediaGeral).classificacao}
      </Typography>
    </CardContent>
  </Card>
</Box>

<Box
  className="dashboard-grid secao-contexto"
  sx={{
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      lg: "1fr 1fr",
    },
    gap: 3,
    mb: 3,
  }}
>
  <Card className="evitar-quebra card-contexto" variant="outlined" sx={{ borderRadius: 3 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={800} mb={2}>
        Contexto Geral da Empresa
      </Typography>

      <Typography color="text.secondary" mb={2}>
        Resultado médio consolidado por dimensão psicossocial
      </Typography>

     <Box
       className="grafico-contexto"
       data-grafico-impressao="contexto"
       sx={{ width: "100%", height: 220 }}
     >
        <ResponsiveContainer>
          <BarChart
            data={resumoDimensoes}
            margin={{ top: 8, right: 15, left: 0, bottom: 25 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="nome"
              angle={-25}
              textAnchor="end"
              interval={0}
              height={35}
              tick={{ fontSize: 9 }}
            />
            <YAxis domain={[0, 25]} />
            <Tooltip />

            <Bar
              dataKey="media"
              name="Resultado médio"
              radius={[4, 4, 0, 0]}
              isAnimationActive={!modoVisualizacao}
            >
              {resumoDimensoes.map((item, idx) => (
                <Cell
                  key={idx}
                  fill={obterCorRisco(item.media)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <ImagemGraficoImpressao
          src={graficosImpressao.contexto}
          alt="Contexto Geral da Empresa"
        />
      </Box>
    </CardContent>
  </Card>

  <Card className="evitar-quebra card-faixas" variant="outlined" sx={{ borderRadius: 3 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={800} mb={2}>
        Distribuição por Faixa de Risco
      </Typography>
      <Typography color="text.secondary" mb={2}>
        Quantidade de dimensões classificadas em cada faixa de risco
      </Typography>

      <Box
        className="grafico-faixas"
        data-grafico-impressao="faixas"
        sx={{ width: "100%", height: 260 }}
      >
        <ResponsiveContainer>
          <ComposedChart
            data={distribuicaoRisco}
            margin={{ top: 10, right: 15, left: 0, bottom: 15 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="risco" />
            <YAxis allowDecimals={false} />
            <Tooltip content={<TooltipDistribuicaoDimensoes />} />

            <Bar
              dataKey="quantidade"
              name="Quantidade de dimensões"
              radius={[4, 4, 0, 0]}
              isAnimationActive={!modoVisualizacao}
            >
              {distribuicaoRisco.map((item, idx) => (
                <Cell
                  key={idx}
                  fill={item.cor}
                />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
        <ImagemGraficoImpressao
          src={graficosImpressao.faixas}
          alt="Distribuição por Faixa de Risco"
        />
      </Box>
    </CardContent>
  </Card>
</Box>

<Card
  className="nao-imprimir"
  variant="outlined"
  sx={{ borderRadius: 3, mb: 3 }}
>
    <CardContent>
      <Typography variant="h6" fontWeight={800}>
        Análise por Setor e Função
      </Typography>
      <Typography color="text.secondary" mb={2}>
        Selecione uma dimensão para identificar os grupos mais críticos
      </Typography>

      <FormControl fullWidth size="small">
        <InputLabel>Dimensão psicossocial</InputLabel>
        <Select
          value={dimensaoSelecionada}
          label="Dimensão psicossocial"
          onChange={(event) => setDimensaoSelecionada(event.target.value)}
        >
          {DIMENSOES_PSICOSSOCIAIS.map((dimensao) => (
            <MenuItem key={dimensao.id} value={dimensao.id}>
              {dimensao.nome}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </CardContent>
</Card>

<Box
  className="dashboard-grid"
  sx={{
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      lg: "1fr 1fr",
    },
    gap: 3,
    mb: 3,
  }}
>
  <Card className="evitar-quebra card-setor" variant="outlined">
    <CardContent>
      <Typography variant="h6" fontWeight={800}>
        Risco por Setor
      </Typography>
      <Typography color="text.secondary" mb={2}>
        {nomeDimensaoSelecionada}
      </Typography>

      <Box
        className="grafico-grupo"
        data-grafico-impressao="setor"
        sx={{ width: "100%", height: 260 }}
      >
        <ResponsiveContainer>
          <ComposedChart
            data={graficoSetores}
            margin={{ top: 5, right: 10, left: 0, bottom: 25 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="setor"
              angle={-15}
              textAnchor="end"
              interval={0}
              height={35}
              tick={{ fontSize: 9 }}
            />
            <YAxis domain={[0, 25]} />
            <Tooltip />

            <Bar
              dataKey="media"
              name="Resultado médio"
              radius={[4, 4, 0, 0]}
              isAnimationActive={!modoVisualizacao}
            >
              {graficoSetores.map((item, idx) => (
                <Cell
                  key={idx}
                  fill={obterCorRisco(item.media)}
                />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
        <ImagemGraficoImpressao
          src={graficosImpressao.setor}
          alt="Risco por Setor"
        />
      </Box>
    </CardContent>
  </Card>

  <Card className="evitar-quebra card-funcao" variant="outlined">
    <CardContent>
      <Typography variant="h6" fontWeight={800}>
        Risco por Função
      </Typography>
      <Typography color="text.secondary" mb={2}>
        {nomeDimensaoSelecionada}
      </Typography>

      <Box
        className="grafico-grupo"
        data-grafico-impressao="funcao"
        sx={{ width: "100%", height: 260 }}
      >
        <ResponsiveContainer>
          <ComposedChart
            data={graficoFuncoes}
            margin={{ top: 5, right: 10, left: 0, bottom: 25 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="funcao"
              angle={-15}
              textAnchor="end"
              interval={0}
              height={35}
              tick={{ fontSize: 9 }}
            />
            <YAxis domain={[0, 25]} />
            <Tooltip />

            <Bar
              dataKey="media"
              name="Resultado médio"
              radius={[4, 4, 0, 0]}
              isAnimationActive={!modoVisualizacao}
            >
              {graficoFuncoes.map((item, idx) => (
                <Cell
                  key={idx}
                  fill={obterCorRisco(item.media)}
                />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
        <ImagemGraficoImpressao
          src={graficosImpressao.funcao}
          alt="Risco por Função"
        />
      </Box>
    </CardContent>
  </Card>
</Box>

<Card className="evitar-quebra card-idade" variant="outlined">
  <CardContent>
    <Typography variant="h6" fontWeight={800}>
      Idade
    </Typography>
    <Typography color="text.secondary" mb={1}>
      {respostasFiltradas.length} respostas
    </Typography>

    <Box
      className="grafico-demografico"
      data-grafico-impressao="idade"
      sx={{ width: "100%", height: 230 }}
    >
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={distribuicaoIdade}
            dataKey="quantidade"
            nameKey="nome"
            cx="38%"
            cy="50%"
            outerRadius="78%"
            label={percentualPizza}
            labelLine={false}
            isAnimationActive={!modoVisualizacao}
          >
            {distribuicaoIdade.map((item) => (
              <Cell key={item.nome} fill={item.cor} />
            ))}
          </Pie>
          <Tooltip />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            formatter={(value) => (
              <span style={{ color: "#333", fontSize: 11 }}>{value}</span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
      <ImagemGraficoImpressao
        src={graficosImpressao.idade}
        alt="Distribuição por idade"
      />
    </Box>
  </CardContent>
</Card>

<Card className="evitar-quebra card-sexo" variant="outlined">
  <CardContent>
    <Typography variant="h6" fontWeight={800}>
      Sexo
    </Typography>
    <Typography color="text.secondary" mb={1}>
      {respostasFiltradas.length} respostas
    </Typography>

    <Box
      className="grafico-demografico"
      data-grafico-impressao="sexo"
      sx={{ width: "100%", height: 230 }}
    >
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={distribuicaoSexo}
            dataKey="quantidade"
            nameKey="nome"
            cx="38%"
            cy="50%"
            outerRadius="78%"
            label={percentualPizza}
            labelLine={false}
            isAnimationActive={!modoVisualizacao}
          >
            {distribuicaoSexo.map((item) => (
              <Cell key={item.nome} fill={item.cor} />
            ))}
          </Pie>
          <Tooltip />
          <Legend
            layout="vertical"
            align="right"
            verticalAlign="middle"
            formatter={(value) => (
              <span style={{ color: "#333", fontSize: 11 }}>{value}</span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
      <ImagemGraficoImpressao
        src={graficosImpressao.sexo}
        alt="Distribuição por sexo"
      />
    </Box>
  </CardContent>
</Card>

</Box>

        </Box>

        </Box>

<Box className="quebra-pagina pagina-dados" sx={{ mb: 3 }}>
  <Typography variant="h5" fontWeight={800} mb={2}>
    Dados Consolidados
  </Typography>

  <Card className="evitar-quebra" variant="outlined" sx={{ mb: 3 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={800} mb={2}>
        Resultado por Dimensão
      </Typography>

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Dimensão</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Respostas</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Resultado médio</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Faixa</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Classificação</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {resumoDimensoes.map((dimensao) => {
              const risco = classificarRiscoMatriz(dimensao.media);

              return (
                <TableRow key={dimensao.nome}>
                  <TableCell>{dimensao.nome}</TableCell>
                  <TableCell>{dimensao.totalRespostas}</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>
                    {dimensao.media}
                  </TableCell>
                  <TableCell>{risco.faixa}</TableCell>
                  <TableCell>
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        bgcolor: risco.cor,
                        color: obterCorTextoClassificacao(risco),
                        borderRadius: 1,
                        px: 1,
                        py: 0.4,
                        fontWeight: 800,
                      }}
                    >
                      {risco.label} - {risco.classificacao}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </CardContent>
  </Card>

  <Card className="evitar-quebra" variant="outlined" sx={{ mb: 3 }}>
    <CardContent>
      <Typography variant="h6" fontWeight={800} mb={2}>
        Distribuição das Dimensões por Faixa
      </Typography>

      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Faixa de risco</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Quantidade</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Dimensões</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {distribuicaoRisco.map((faixa) => (
              <TableRow key={faixa.risco}>
                <TableCell sx={{ fontWeight: 800 }}>{faixa.risco}</TableCell>
                <TableCell>{faixa.quantidade}</TableCell>
                <TableCell>
                  {faixa.dimensoes
                    .map(
                      (dimensao) => `${dimensao.nome} (${dimensao.media})`
                    )
                    .join("; ")}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </CardContent>
  </Card>

  <Box
    className="tabelas-grupos"
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
      gap: 3,
    }}
  >
    <Card className="evitar-quebra" variant="outlined">
      <CardContent>
        <Typography variant="h6" fontWeight={800}>
          Resultado por Setor
        </Typography>
        <Typography color="text.secondary" mb={2}>
          Dimensão: {nomeDimensaoSelecionada}
        </Typography>

        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Setor</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Pessoas</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Média</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Risco</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {graficoSetores.map((item) => {
                const risco = classificarRiscoMatriz(item.media);

                return (
                  <TableRow key={item.setor}>
                    <TableCell>{item.setor}</TableCell>
                    <TableCell>{item.total}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{item.media}</TableCell>
                    <TableCell>{risco.label}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>

    <Card className="evitar-quebra" variant="outlined">
      <CardContent>
        <Typography variant="h6" fontWeight={800}>
          Resultado por Função
        </Typography>
        <Typography color="text.secondary" mb={2}>
          Dimensão: {nomeDimensaoSelecionada}
        </Typography>

        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Função</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Pessoas</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Média</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Risco</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {graficoFuncoes.map((item) => {
                const risco = classificarRiscoMatriz(item.media);

                return (
                  <TableRow key={item.funcao}>
                    <TableCell>{item.funcao}</TableCell>
                    <TableCell>{item.total}</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>{item.media}</TableCell>
                    <TableCell>{risco.label}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </CardContent>
    </Card>
  </Box>
</Box>

<Card className="nao-imprimir" variant="outlined" sx={{ mb: 3 }}>
  <CardContent>
    <Typography variant="h6" fontWeight={800} mb={2}>
      Respostas por Respondente
    </Typography>

    <Stack spacing={1.5}>
      {respostasIndividuais.map((resposta, index) => (
        <Accordion key={resposta.id} disableGutters defaultExpanded={index === 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.5, sm: 2 }}
              sx={{ width: "100%" }}
            >
              <Typography fontWeight={800}>
                {resposta.respondenteLabel}
              </Typography>
            </Stack>
          </AccordionSummary>

          <AccordionDetails>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800 }}>
                      <TableSortLabel
                        active={ordenacaoTabela.campo === "pergunta"}
                        direction={ordenacaoTabela.direcao}
                        onClick={() => alterarOrdenacaoTabela("pergunta")}
                      >
                        Pergunta
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, width: 120 }}>Probabilidade</TableCell>
                    <TableCell sx={{ fontWeight: 800, width: 110 }}>Severidade</TableCell>
                    <TableCell sx={{ fontWeight: 800, width: 90 }}>
                      <TableSortLabel
                        active={ordenacaoTabela.campo === "resultado"}
                        direction={ordenacaoTabela.direcao}
                        onClick={() => alterarOrdenacaoTabela("resultado")}
                      >
                        Resultado
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, width: 150 }}>
                      <TableSortLabel
                        active={ordenacaoTabela.campo === "risco"}
                        direction={ordenacaoTabela.direcao}
                        onClick={() => alterarOrdenacaoTabela("risco")}
                      >
                        Risco
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800, width: 100 }}>Matriz</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {resposta.matriz.map((item) => (
                    <TableRow key={`${resposta.id}-${item.codigo}`}>
                      <TableCell>
                        <Typography fontWeight={700} component="span">
                          {item.codigo}
                        </Typography>
                        {item.perguntaTexto ? ` - ${item.perguntaTexto}` : ""}
                      </TableCell>
                      <TableCell>{item.probabilidade}</TableCell>
                      <TableCell>{item.severidade}</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>
                        {item.risco}
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            borderRadius: 1,
                            px: 1,
                            py: 0.4,
                            fontSize: 12,
                            fontWeight: 800,
                            bgcolor: item.classificacao.cor,
                            color: obterCorTextoClassificacao(item.classificacao),
                          }}
                        >
                          {item.classificacao.label}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ whiteSpace: "nowrap" }}
                          onClick={() => setMatrizSelecionada(item)}
                        >
                          Ver matriz
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  </CardContent>
</Card>

</>
)}
</Paper>



      <Dialog
        open={Boolean(matrizSelecionada)}
        onClose={() => setMatrizSelecionada(null)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Matriz 5x5 - Pergunta {matrizSelecionada?.codigo}
        </DialogTitle>

        <DialogContent>
          {matrizSelecionada && (
            <>
              <Typography mb={2}>
                Probabilidade {matrizSelecionada.probabilidade} × Severidade{" "}
                {matrizSelecionada.severidade} ={" "}
                <strong>{matrizSelecionada.risco}</strong> —{" "}
                {matrizSelecionada.classificacao.label}
              </Typography>

              <MatrizRiscoVisual item={matrizSelecionada} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
